import { type InfiniteData, QueryClient } from '@tanstack/react-query'
import type { CursorPaginationMeta, OffsetPaginationMeta } from './gen'

const queryClientConfig = {
	defaultOptions: {
		queries: {
			networkMode: 'online',
			retry: typeof window === 'undefined' ? 0 : retryOnlyAHiccup,
			retryOnMount: true,
			retryDelay: 1000,
			staleTime: 600_000, //10 min
			refetchOnMount: true,
			refetchOnWindowFocus: false,
			refetchOnReconnect: true
		}
	}
} satisfies ConstructorParameters<typeof QueryClient>[0]

/**
 * One more try for a hiccup, none for a verdict (OPSSTD-5836).
 *
 * A refusal the server gives on purpose — `not_found` on a folder that is no
 * longer reachable, a missing role, an invalid request, a conflict — answers
 * the same the second time. Retrying it only delayed the error, and when the
 * tab was unfocused or the browser thought itself offline the retry never
 * started: the read stayed «in progress» with no error for whoever was waiting
 * on it. Those are the `denied`, `invalid` and `conflict` categories. A `fault`
 * is the server's own failure and a `quota` is a rate limit that may have
 * passed a second later, so both keep their one retry, and so does an error
 * with no body at all, which is the network.
 */
function retryOnlyAHiccup(failureCount: number, error: unknown) {
	const verdict =
		typeof error === 'object' &&
		error !== null &&
		'category' in error &&
		VERDICTS.has(error.category)

	return !verdict && failureCount < 1
}

const VERDICTS = new Set<unknown>(['conflict', 'denied', 'invalid'])

export function createAppQueryClient() {
	return new QueryClient(queryClientConfig)
}

// The guarded `fetchNextPage` for a scroll or load-more trigger. It returns
// what the fetch settled to, and `undefined` when the guard refused to fetch,
// so a caller that has to read the outcome — a cursor endpoint answering
// `cursor_invalid`, which asks the list to restart from the first page rather
// than to show an error — can do it without restating the guard.
export const infiniteQueryFetchNextPage = async <TResult>(infiniteQuery: {
	hasNextPage: boolean
	isFetching: boolean
	fetchNextPage: () => Promise<TResult>
}) => {
	if (infiniteQuery.hasNextPage && !infiniteQuery.isFetching) {
		return await infiniteQuery.fetchNextPage()
	}
	return undefined
}

// `getNextPageParam` for cursor-paginated endpoints (`CursorPaginationMeta`):
// the opaque `nextCursor`, or `null` on the last page. Pair with
// `initialPageParam: null`.
export const cursorNextPageParam = (lastPage: { meta: CursorPaginationMeta }) =>
	lastPage.meta.nextCursor

// `getNextPageParam` for offset-paginated endpoints (`OffsetPaginationMeta`):
// the next 1-based page number, or `null` on the last page. Pair with
// `initialPageParam: 1`.
export const offsetNextPageParam = (lastPage: {
	meta: OffsetPaginationMeta
}) =>
	lastPage.meta.page < lastPage.meta.totalPages
		? lastPage.meta.page + 1
		: null

// Collapses an infinite query's pages into one list. Belongs in a factory's
// `select` — the one view `select` is allowed to hold that the `queryFn` cannot.
export const flattenInfinitePages = <T>(data: InfiniteData<{ data: T[] }>) =>
	data.pages.flatMap((page) => page.data)
